import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, GraduationCap, LayoutGrid, List, Search, SlidersHorizontal, Users, Award } from "lucide-react";
import { useFormationList, useFormationCategories } from "@/hooks/useFormations";
import { FormationCard, FormationCardSkeleton } from "@/components/FormationCard";
import { DynamicIcon } from "@/lib/icons";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSeo } from "@/lib/seo";

const HERO = "https://static.prod-images.emergentagent.com/jobs/bd4fdd39-8472-4aaf-94d1-f48a77c5d491/images/f5f1ec553aa907b27154684c602106a062dbf00facf15b54455047615257d317.jpeg";

const LEVELS = ["Débutant", "Intermédiaire", "Avancé", "Tous niveaux"];
const SORTS = [
  { value: "recent", label: "Plus récentes" },
  { value: "popular", label: "Plus populaires" },
  { value: "price_asc", label: "Prix croissant" },
  { value: "price_desc", label: "Prix décroissant" },
];

export default function Formations() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");
  const [sort, setSort] = useState("recent");
  const [view, setView] = useState("grid");
  const category = params.get("categorie") || "all";

  const { data: catData } = useFormationCategories();
  const categories = catData?.categories || [];
  const { data, isLoading } = useFormationList({
    q, category: category === "all" ? "" : category,
    level: level === "all" ? "" : level, sort,
  });
  const items = data?.items || [];

  const totalFormations = catData?.total || 0;
  const totalStudents = useMemo(() => items.reduce((s, i) => s + (i.students_count || 0), 0), [items]);

  useSeo({
    title: "Formations en ligne — ISMO-TECH Academy",
    description:
      "Développez vos compétences avec ISMO-TECH Academy : informatique, bureautique, sécurité, développement et plus. Formations en ligne par des experts, avec certificat.",
  });

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("categorie");
    else next.set("categorie", slug);
    setParams(next);
  };

  return (
    <div className="bg-surface">
      {/* HERO */}
      <section className="relative overflow-hidden bg-night">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-night via-night/85 to-night/30" />
        <div className="hero-grid-lines absolute inset-0 opacity-40" />
        <div className="container-it relative py-14 lg:py-20">
          <span className="label-eyebrow text-brand-light">ISMO-TECH Academy</span>
          <h1 className="mt-3 max-w-2xl font-display text-4xl font-black leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            Formations en ligne
          </h1>
          <p className="mt-4 max-w-xl text-base text-slate-300 lg:text-lg">
            Développez vos compétences grâce à nos formations complètes dispensées par des experts.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {[
              { icon: GraduationCap, value: `+${Math.max(totalFormations, 120)}`, label: "Formations" },
              { icon: Users, value: "+8 500", label: "Apprenants" },
              { icon: Award, value: "+25", label: "Experts formateurs" },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-3 rounded-2xl glass-dark px-5 py-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand text-white">
                  <s.icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-display text-xl font-extrabold text-white">{s.value}</span>
                  <span className="block text-xs text-slate-300">{s.label}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLBAR */}
      <div className="container-it -mt-6 relative z-10">
        <div className="flex flex-col gap-3 rounded-2xl border border-line bg-white p-3 shadow-lg lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="formation-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher une formation..."
              className="h-11 w-full rounded-xl border border-line pl-10 pr-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <div className="flex gap-2 lg:hidden">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-11 flex-1" data-testid="formation-category-mobile"><SelectValue placeholder="Catégories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="h-11 w-full lg:w-40" data-testid="formation-level">
                <SlidersHorizontal className="mr-1.5 h-4 w-4 text-slate-400" />
                <SelectValue placeholder="Niveau" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les niveaux</SelectItem>
                {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-11 w-full lg:w-44" data-testid="formation-sort"><SelectValue placeholder="Trier par" /></SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="hidden items-center gap-1 rounded-xl border border-line p-1 lg:flex">
              <button data-testid="formation-view-grid" onClick={() => setView("grid")} className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${view === "grid" ? "bg-brand text-white" : "text-slate-400 hover:text-ink"}`}>
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button data-testid="formation-view-list" onClick={() => setView("list")} className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${view === "list" ? "bg-brand text-white" : "text-slate-400 hover:text-ink"}`}>
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="container-it grid gap-6 py-8 lg:grid-cols-[240px_1fr]">
        {/* SIDEBAR */}
        <aside className="hidden lg:block">
          <div className="sticky top-[100px] rounded-2xl border border-line bg-white p-4">
            <p className="mb-3 font-display text-sm font-bold text-ink">Catégories</p>
            <div className="space-y-1">
              <button
                data-testid="formation-cat-all"
                onClick={() => setCategory("all")}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${category === "all" ? "bg-brand text-white" : "text-slate-600 hover:bg-brand-soft"}`}
              >
                <span className="flex items-center gap-2"><BookOpen className="h-4 w-4" /> Toutes les formations</span>
                <span className={`rounded-md px-1.5 text-[11px] font-bold ${category === "all" ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{totalFormations}</span>
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  data-testid={`formation-cat-${c.slug}`}
                  onClick={() => setCategory(c.slug)}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${category === c.slug ? "bg-brand text-white" : "text-slate-600 hover:bg-brand-soft"}`}
                >
                  <span className="flex items-center gap-2">
                    <span style={{ color: category === c.slug ? "#fff" : c.color }}><DynamicIcon name={c.icon} className="h-4 w-4" /></span>
                    {c.name}
                  </span>
                  <span className={`rounded-md px-1.5 text-[11px] font-bold ${category === c.slug ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{c.count}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* GRID */}
        <div>
          {isLoading ? (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <FormationCardSkeleton key={i} />)}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-white py-20 text-center">
              <GraduationCap className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 font-display text-lg font-bold text-ink">Aucune formation trouvée</p>
              <p className="mt-1 text-sm text-slate-500">Essayez une autre recherche ou catégorie.</p>
            </div>
          ) : (
            <>
              <p className="mb-4 text-sm text-slate-500" data-testid="formation-count">
                <span className="font-bold text-ink">{items.length}</span> formation{items.length > 1 ? "s" : ""} disponible{items.length > 1 ? "s" : ""}
              </p>
              <div className={view === "grid" ? "grid gap-5 sm:grid-cols-2 xl:grid-cols-3" : "grid gap-5 grid-cols-1 md:grid-cols-2"}>
                {items.map((f, i) => <FormationCard key={f.id} formation={f} index={i} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
